import { Button } from "~/atoms/button";
import DocumentIcon from "./DocumentIcon";
import { useState, useEffect } from "react";
import { type OfficeFileType, type OfficeRelease } from "~/api/types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { useTranslation } from "react-i18next";
import { analyzeTrackChanges } from "~/helpers/docx-track-changes";
import { downloadArtifact, getOfficeURIScheme } from "~/api/v2/requests";
import { Spinner } from "~/atoms/spinner";
import { DocumentArtifactStatus } from "~/config/types";
import { useSpeEditPermissions } from "~/hooks/useEditPermissions";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/atoms/alert-dialog";

interface Props {
  release: OfficeRelease;
}

export const officeFileTypeToTitle: Record<OfficeFileType, string> = {
  docx: "Word",
  xlsx: "Excel",
  pptx: "PowerPoint",
};

export const OfficeDesktopButton = ({ release }: Props) => {
  const { t } = useTranslation();
  const { canNotEditSpeDraftFile, isSpeUserWithoutSso } =
    useSpeEditPermissions(release);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showTrackChangesWarning, setShowTrackChangesWarning] = useState(false);
  const [isCheckingTrackChanges, setIsCheckingTrackChanges] = useState(false);
  const [isOpeningDocument, setIsOpeningDocument] = useState(false);

  // Check track changes proactively in the background
  useEffect(() => {
    const checkTrackChanges = async () => {
      const shouldCheck =
        release.artifact.status === DocumentArtifactStatus.draft &&
        release.artifact.fileType === "docx";

      if (!shouldCheck) return;

      setIsCheckingTrackChanges(true);
      try {
        const blob = await downloadArtifact(release.artifact.id);
        const status = await analyzeTrackChanges(blob);
        setShowTrackChangesWarning(!status.hasTrackRevisionsSetting);
      } catch {
        setShowTrackChangesWarning(false);
      } finally {
        setIsCheckingTrackChanges(false);
      }
    };

    void checkTrackChanges();
  }, [release.artifact.status, release.artifact.fileType, release.artifact.id]);

  const openInOffice = async () => {
    setIsOpeningDocument(true);
    try {
      const { uri } = await getOfficeURIScheme(release.artifact.id);
      if (!uri) return;
      window.location.href = uri;
    } finally {
      setIsOpeningDocument(false);
    }
  };

  const handleOpenInOffice = async () => {
    if (isCheckingTrackChanges) {
      return;
    }

    if (!showTrackChangesWarning) {
      await openInOffice();
    } else {
      setIsDialogOpen(true);
    }
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    await openInOffice();
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const isLoading = isCheckingTrackChanges || isOpeningDocument;

  if (isSpeUserWithoutSso || canNotEditSpeDraftFile) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-block w-fit">
              <Button variant="outline" disabled>
                <DocumentIcon
                  type={release.artifact.fileType}
                  variant="product"
                />
                {t(($) => $.commons.workflow.openInOffice, {
                  officeType: officeFileTypeToTitle[release.artifact.fileType],
                })}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isSpeUserWithoutSso
                ? t(($) => $.commons.auth.ssoRequiredToEdit)
                : t(($) => $.commons.auth.cantEditSpeDraftFile)}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isLoading) {
    return (
      <Button variant="outline" disabled>
        <Spinner className="text-info size-4" />
        {t(($) => $.commons.workflow.openInOffice, {
          officeType: officeFileTypeToTitle[release.artifact.fileType],
        })}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleOpenInOffice}
        disabled={isLoading}
      >
        {isLoading ? (
          <Spinner className="text-info size-4" />
        ) : (
          <DocumentIcon type={release.artifact.fileType} variant="product" />
        )}
        {t(($) => $.commons.workflow.openInOffice, {
          officeType: officeFileTypeToTitle[release.artifact.fileType],
        })}
      </Button>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {t(($) => $.modal.officeDesktop.trackChangesDisabledTitle)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(($) => $.modal.officeDesktop.trackChangesDisabledDescription)}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isOpeningDocument}
            >
              {t(($) => $.modal.officeDesktop.cancel)}
            </Button>
            <Button
              variant="outline"
              onClick={handleConfirm}
              disabled={isOpeningDocument}
            >
              {isOpeningDocument ? (
                <Spinner className="size-4" />
              ) : (
                <DocumentIcon
                  type={release.artifact.fileType}
                  variant="product"
                />
              )}
              {t(($) => $.commons.workflow.openInOffice, {
                officeType: officeFileTypeToTitle[release.artifact.fileType],
              })}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
